import React from 'react';
import styled, { css } from 'styled-components';
import { blockPadV, blockPadH, blockW } from 'theme/mixins/padding';
import type { QuoteShowcase as T } from './types';

const Wrapper = styled.div`
  ${() => blockPadV()};
  ${() => blockPadH()};
`;

const Container = styled.div`
  ${() => blockW()};
`;

const Content = styled.div`
  padding-top: 1rem;
  padding-bottom: 1rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  max-width: 85%;
  ${({ theme }) => theme.mq.lg`
    max-width: 66%;
  `}
`;

const Quote = styled.h4``;

const CreditedTo = styled.p`
  padding-top: 1rem;
`;

interface BackgroundInterface {
  bgImage: string;
}

const Background = styled.div<BackgroundInterface>`
  min-height: 70vh;
  border-radius: 2.75rem;
  background: ${({ bgImage }) => (bgImage ? `url(${bgImage})` : `url('/uploads/GFX_bg01.png')`)};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 1.5rem;

  ${({ theme }) => theme.mq.md`
    height: 45.25rem;
    border-radius: 7.5rem;
    padding: 4rem;
  `}
`;

const alignments = ['top', 'left', 'bottom', 'right'] as const;

interface IFrameLine {
  align: typeof alignments[number];
}

interface IImageFrame {
  children?: React.ReactNode;
  bgImage?: string;
}

const FrameLine = styled.div<IFrameLine>`
  position: absolute;
  mix-blend-mode: difference;

  ${({ align }) => {
    switch (align) {
      case 'top':
        return css`
          top: 1.5rem;
          background: white;
          width: 100%;
          height: 1px;
          ${({ theme }) => theme.mq.md`
            top: 6rem;
          `}
        `;
      case 'left':
        return css`
          left: 1.5rem;
          background: white;
          width: 1px;
          height: 100%;
          ${({ theme }) => theme.mq.md`
            left: 6rem;
          `}
        `;
      case 'bottom':
        return css`
          bottom: 1.5rem;
          background: white;
          width: 100%;
          height: 1px;
          ${({ theme }) => theme.mq.md`
            bottom: 6rem;
          `}
        `;
      case 'right':
        return css`
          right: 1.5rem;
          background: white;
          width: 1px;
          height: 100%;
          ${({ theme }) => theme.mq.md`
            right: 6rem;
          `}
        `;
      default:
        break;
    }
  }};
`;

const ImageFrame: React.FC<IImageFrame> = ({ children, bgImage }: IImageFrame) => {
  return (
    <Background bgImage={bgImage || ''}>
      <FrameLine align="top" />
      <FrameLine align="left" />
      <FrameLine align="bottom" />
      <FrameLine align="right" />
      {children}
    </Background>
  );
};

const QuoteShowcase: React.FC<T> = ({ text, subtext, asset }) => {
  const bgImage = asset?.src;
  return (
    <Wrapper>
      <Container>
        <ImageFrame bgImage={bgImage || ''}>
          <Content>
            {text && <Quote>{text}</Quote>}
            {subtext && <CreditedTo>{subtext}</CreditedTo>}
          </Content>
        </ImageFrame>
      </Container>
    </Wrapper>
  );
};

export default QuoteShowcase;
